import { ArrowPath, ArrowUturnLeft, Bolt, Puzzle, Star } from "@medusajs/icons"
import type { NavItem } from "@/lib/nav"
import type { Me } from "@/lib/types"

/**
 * Terify apps in the admin. Each app's screens live in src/apps/<id> and are
 * loaded only when opened, and only while the app is switched on in
 * Settings → Apps (the backend reports which are on in /me).
 */
export type AdminApp = { id: string; nav: NavItem }

export const ADMIN_APPS: AdminApp[] = [
  {
    id: "reviews",
    nav: {
      to: "/apps/reviews",
      label: "Reviews",
      icon: Star,
      perm: "reviews:read",
      children: [
        { to: "/apps/reviews/reviews", label: "Reviews", perm: "reviews:read" },
        { to: "/apps/reviews/products", label: "Products", perm: "reviews:read" },
        { to: "/apps/reviews/requests", label: "Requests", perm: "reviews:read" },
        { to: "/apps/reviews/import", label: "Import", perm: "reviews:read" },
        { to: "/apps/reviews/settings", label: "Settings", perm: "reviews:read" },
      ],
    },
  },
  {
    id: "subscriptions",
    nav: {
      to: "/apps/subscriptions",
      label: "Subscriptions",
      icon: ArrowPath,
      perm: "subscriptions:read",
      children: [
        { to: "/apps/subscriptions/subscriptions", label: "Subscriptions", perm: "subscriptions:read" },
        { to: "/apps/subscriptions/retention", label: "Retention", perm: "subscriptions:read" },
        { to: "/apps/subscriptions/upcoming", label: "Upcoming charges", perm: "subscriptions:read" },
        { to: "/apps/subscriptions/payments", label: "Failed payments", perm: "subscriptions:read" },
        { to: "/apps/subscriptions/plans", label: "Plans", perm: "subscriptions:read" },
        { to: "/apps/subscriptions/import", label: "Import", perm: "subscriptions:read" },
        // Subscriptions → Settings is store configuration (offers, tiers, retries, descriptor…): settings:read to see it.
        { to: "/apps/subscriptions/settings", label: "Settings", perm: "settings:read" },
      ],
    },
  },
  {
    id: "upsells",
    nav: {
      to: "/apps/upsells",
      label: "Cart & Checkout",
      icon: Bolt,
      perm: "upsells:read",
      children: [
        { to: "/apps/upsells/offers", label: "Offers", perm: "upsells:read" },
        { to: "/apps/upsells/products", label: "Product blocks", perm: "upsells:read" },
        { to: "/apps/upsells/settings", label: "Settings", perm: "upsells:read" },
      ],
    },
  },
  {
    id: "recovery",
    nav: {
      to: "/apps/recovery",
      label: "Recovery",
      icon: ArrowUturnLeft,
      perm: "subscriptions:read",
      children: [
        { to: "/apps/recovery/customers", label: "Customers", perm: "subscriptions:read" },
        { to: "/apps/recovery/messages", label: "Messages", perm: "subscriptions:read" },
        { to: "/apps/recovery/sends", label: "Sends", perm: "subscriptions:read" },
        { to: "/apps/recovery/settings", label: "Settings", perm: "recovery_settings:read" },
      ],
    },
  },
]

export const APPS_HEADER: NavItem = { to: "/settings/apps", label: "Apps", icon: Puzzle }

export const isAppOn = (me: Me, id: string) => !!me.apps?.find((a) => a.id === id && a.enabled)

/** Sidebar entries for the apps that are switched on. */
export const enabledAppNav = (me: Me) => ADMIN_APPS.filter((a) => isAppOn(me, a.id)).map((a) => a.nav)
